import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createVisaApplication,
  updateVisaApplication,
  getVisaApplication,
  submitVisaApplication,
  getVisaRequirements
} from '../../../api/educentialApi';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Globe,
  User,
  Plane,
  GraduationCap,
  DollarSign,
  Briefcase,
  FileCheck,
  AlertCircle,
  CheckCircle2,
  Loader
} from 'lucide-react';

const VisaApplicationForm = () => {
  const navigate = useNavigate();
  const { applicationId } = useParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [applicationRef, setApplicationRef] = useState('');
  const [requirements, setRequirements] = useState(null);

  const [formData, setFormData] = useState({
    country: '',
    visaType: '',
    personalInfo: {
      fullName: '',
      dateOfBirth: '',
      placeOfBirth: '',
      nationality: '',
      passportNumber: '',
      passportIssueDate: '',
      passportExpiryDate: '',
      phoneNumber: '',
      email: '',
      currentAddress: '',
      permanentAddress: ''
    },
    travelHistory: {
      previousVisas: [],
      visaRefusals: false,
      refusalDetails: '',
      traveledCountries: []
    },
    educationDetails: {
      casOrI20Number: '',
      universityName: '',
      courseName: '',
      courseLevel: '',
      courseStartDate: '',
      courseEndDate: '',
      tuitionFees: ''
    },
    financialInfo: {
      totalFundsAvailable: '',
      sponsorType: '',
      sponsorName: '',
      sponsorRelationship: '',
      sponsorOccupation: '',
      bankName: '',
      accountNumber: ''
    },
    employmentHistory: {
      currentlyEmployed: false,
      employerName: '',
      position: '',
      startDate: '',
      endDate: '',
      salary: ''
    }
  });

  const steps = [
    { num: 1, name: 'Country & Visa Type', icon: Globe },
    { num: 2, name: 'Personal Info', icon: User },
    { num: 3, name: 'Travel History', icon: Plane },
    { num: 4, name: 'Education Details', icon: GraduationCap },
    { num: 5, name: 'Financial Info', icon: DollarSign },
    { num: 6, name: 'Employment', icon: Briefcase },
    { num: 7, name: 'Review & Submit', icon: FileCheck }
  ];

  useEffect(() => {
    if (applicationId) {
      loadApplication();
    }
  }, [applicationId]);

  useEffect(() => {
    if (formData.country) {
      loadRequirements();
    }
  }, [formData.country]);

  const loadApplication = async () => {
    try {
      setLoading(true);
      const response = await getVisaApplication(applicationId);
      if (response.success) {
        const app = response.data;
        setFormData({
          country: app.country,
          visaType: app.visaType,
          personalInfo: app.personalInfo || formData.personalInfo,
          travelHistory: app.travelHistory || formData.travelHistory,
          educationDetails: app.educationDetails || formData.educationDetails,
          financialInfo: app.financialInfo || formData.financialInfo,
          employmentHistory: app.employmentHistory || formData.employmentHistory
        });
      }
    } catch (error) {
      console.error('Failed to load application:', error);
      setError('Failed to load application');
    } finally {
      setLoading(false);
    }
  };

  const loadRequirements = async () => {
    try {
      const response = await getVisaRequirements(formData.country);
      if (response.success) {
        setRequirements(response.data);
      }
    } catch (error) {
      console.error('Failed to load requirements:', error);
    }
  };

  const handleChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleBasicChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return formData.country && formData.visaType;
      case 2:
        const { fullName, dateOfBirth, nationality, passportNumber, passportExpiryDate, email } = formData.personalInfo;
        return fullName && dateOfBirth && nationality && passportNumber && passportExpiryDate && email;
      case 3:
        return true; // Travel history is optional
      case 4:
        const { casOrI20Number, universityName, courseName, courseStartDate } = formData.educationDetails;
        return casOrI20Number && universityName && courseName && courseStartDate;
      case 5:
        const { totalFundsAvailable, sponsorName } = formData.financialInfo;
        return totalFundsAvailable && sponsorName;
      case 6:
        return true; // Employment is optional
      case 7:
        return true; // Review step
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (isStepValid()) {
      setError('');
      setCurrentStep(prev => Math.min(prev + 1, 7));
    } else {
      setError('Please fill out all required fields before continuing');
    }
  };

  const handlePrev = () => {
    setError('');
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSaveDraft = async () => {
    try {
      setSubmitting(true);
      setError('');

      const response = applicationId
        ? await updateVisaApplication(applicationId, formData)
        : await createVisaApplication(formData);

      if (response.success) {
        setError('');
        alert('Draft saved successfully!');
        if (!applicationId && response.data.id) {
          navigate(`/visa/application/${response.data.id}`);
        }
      }
    } catch (error) {
      console.error('Save draft error:', error);
      setError(error.response?.data?.error || 'Failed to save draft');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError('');

      let appId = applicationId;

      // Create or update application first
      if (!applicationId) {
        const createResponse = await createVisaApplication(formData);
        if (!createResponse.success) {
          throw new Error(createResponse.error || 'Failed to create application');
        }
        appId = createResponse.data.id;
      } else {
        await updateVisaApplication(applicationId, formData);
      }

      // Submit application
      const submitResponse = await submitVisaApplication(appId);
      if (submitResponse.success) {
        setApplicationRef(submitResponse.data.applicationRef);
        setSuccess(true);
      } else {
        throw new Error(submitResponse.error || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Submit error:', error);
      setError(error.response?.data?.error || error.message || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Application Submitted Successfully!
          </h2>
          <p className="text-gray-600 mb-6">
            Your visa application has been submitted. Please note your reference number.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-900 font-semibold mb-1">Reference Number</p>
            <p className="text-xl font-mono font-bold text-blue-700">{applicationRef}</p>
          </div>
          <button
            onClick={() => navigate('/visa/applications')}
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
          >
            View My Applications
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Progress Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-200 rounded-lg p-6 sticky top-8">
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                Visa Application
              </h2>
              <div className="space-y-3">
                {steps.map((step) => {
                  const Icon = step.icon;
                  const isActive = currentStep === step.num;
                  const isCompleted = currentStep > step.num;

                  return (
                    <div
                      key={step.num}
                      className={`flex items-center space-x-3 p-3 rounded-lg ${
                        isActive
                          ? 'bg-blue-50 border border-blue-200'
                          : isCompleted
                          ? 'bg-green-50 border border-green-200'
                          : 'bg-gray-50 border border-gray-200'
                      }`}
                    >
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          isActive
                            ? 'bg-blue-600 text-white'
                            : isCompleted
                            ? 'bg-green-600 text-white'
                            : 'bg-white border border-gray-300 text-gray-400'
                        }`}
                      >
                        {isCompleted ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                      </div>
                      <span
                        className={`text-sm font-semibold ${
                          isActive
                            ? 'text-blue-900'
                            : isCompleted
                            ? 'text-green-900'
                            : 'text-gray-500'
                        }`}
                      >
                        {step.name}
                      </span>
                    </div>
                  );
                })}
              </div>

              {requirements && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">
                    {requirements.country} Requirements
                  </h3>
                  <p className="text-xs text-gray-600 mb-2">
                    Processing: {requirements.processingTime}
                  </p>
                  <p className="text-xs text-gray-600">
                    Fee: {requirements.fee}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Main Form */}
          <div className="lg:col-span-3">
            <div className="bg-white border border-gray-200 rounded-lg p-6 md:p-8">
              {error && (
                <div className="mb-6 flex items-start space-x-3 bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
                  <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {/* Step 1: Country & Visa Type */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">
                      Select Country & Visa Type
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Choose your destination country and visa category
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Destination Country *
                      </label>
                      <select
                        value={formData.country}
                        onChange={(e) => handleBasicChange('country', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select Country</option>
                        <option value="UK">United Kingdom</option>
                        <option value="USA">United States</option>
                        <option value="Canada">Canada</option>
                        <option value="Australia">Australia</option>
                      </select>
                    </div>

                    {formData.country && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Visa Type *
                        </label>
                        <select
                          value={formData.visaType}
                          onChange={(e) => handleBasicChange('visaType', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Select Visa Type</option>
                          {formData.country === 'UK' && (
                            <option value="Student Visa (Tier 4)">Student Visa (Tier 4)</option>
                          )}
                          {formData.country === 'USA' && (
                            <option value="F-1 Student Visa">F-1 Student Visa</option>
                          )}
                          {formData.country === 'Canada' && (
                            <option value="Study Permit">Study Permit</option>
                          )}
                          {formData.country === 'Australia' && (
                            <option value="Student Visa (Subclass 500)">Student Visa (Subclass 500)</option>
                          )}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Step 2: Personal Info */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">
                      Personal Information
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Enter your details exactly as they appear on your passport
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Full Legal Name *
                      </label>
                      <input
                        type="text"
                        value={formData.personalInfo.fullName}
                        onChange={(e) => handleChange('personalInfo', 'fullName', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="As on passport"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Date of Birth *
                      </label>
                      <input
                        type="date"
                        value={formData.personalInfo.dateOfBirth}
                        onChange={(e) => handleChange('personalInfo', 'dateOfBirth', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Place of Birth
                      </label>
                      <input
                        type="text"
                        value={formData.personalInfo.placeOfBirth}
                        onChange={(e) => handleChange('personalInfo', 'placeOfBirth', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Nationality *
                      </label>
                      <input
                        type="text"
                        value={formData.personalInfo.nationality}
                        onChange={(e) => handleChange('personalInfo', 'nationality', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Passport Number *
                      </label>
                      <input
                        type="text"
                        value={formData.personalInfo.passportNumber}
                        onChange={(e) => handleChange('personalInfo', 'passportNumber', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Passport Issue Date
                      </label>
                      <input
                        type="date"
                        value={formData.personalInfo.passportIssueDate}
                        onChange={(e) => handleChange('personalInfo', 'passportIssueDate', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Passport Expiry Date *
                      </label>
                      <input
                        type="date"
                        value={formData.personalInfo.passportExpiryDate}
                        onChange={(e) => handleChange('personalInfo', 'passportExpiryDate', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        value={formData.personalInfo.email}
                        onChange={(e) => handleChange('personalInfo', 'email', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={formData.personalInfo.phoneNumber}
                        onChange={(e) => handleChange('personalInfo', 'phoneNumber', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Current Address
                      </label>
                      <textarea
                        value={formData.personalInfo.currentAddress}
                        onChange={(e) => handleChange('personalInfo', 'currentAddress', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows="2"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Travel History */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">
                      Travel History
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Provide details about your previous travel and visa history
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Have you ever been refused a visa?
                      </label>
                      <div className="flex items-center space-x-6">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            checked={formData.travelHistory.visaRefusals === true}
                            onChange={() => handleChange('travelHistory', 'visaRefusals', true)}
                            className="w-4 h-4 text-blue-600"
                          />
                          <span className="ml-2 text-sm text-gray-700">Yes</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            checked={formData.travelHistory.visaRefusals === false}
                            onChange={() => handleChange('travelHistory', 'visaRefusals', false)}
                            className="w-4 h-4 text-blue-600"
                          />
                          <span className="ml-2 text-sm text-gray-700">No</span>
                        </label>
                      </div>
                    </div>

                    {formData.travelHistory.visaRefusals && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Please provide details
                        </label>
                        <textarea
                          value={formData.travelHistory.refusalDetails}
                          onChange={(e) => handleChange('travelHistory', 'refusalDetails', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          rows="4"
                          placeholder="Country, date, and reason for refusal"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Step 4: Education Details */}
              {currentStep === 4 && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">
                      Education Details
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Enter your course and university information
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {formData.country === 'USA' ? 'I-20 Number' : 'CAS Number'} *
                      </label>
                      <input
                        type="text"
                        value={formData.educationDetails.casOrI20Number}
                        onChange={(e) => handleChange('educationDetails', 'casOrI20Number', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        University Name *
                      </label>
                      <input
                        type="text"
                        value={formData.educationDetails.universityName}
                        onChange={(e) => handleChange('educationDetails', 'universityName', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Course Name *
                      </label>
                      <input
                        type="text"
                        value={formData.educationDetails.courseName}
                        onChange={(e) => handleChange('educationDetails', 'courseName', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Course Level
                      </label>
                      <select
                        value={formData.educationDetails.courseLevel}
                        onChange={(e) => handleChange('educationDetails', 'courseLevel', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select Level</option>
                        <option value="Undergraduate">Undergraduate</option>
                        <option value="Masters">Masters</option>
                        <option value="PhD">PhD</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Course Start Date *
                      </label>
                      <input
                        type="date"
                        value={formData.educationDetails.courseStartDate}
                        onChange={(e) => handleChange('educationDetails', 'courseStartDate', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Course End Date
                      </label>
                      <input
                        type="date"
                        value={formData.educationDetails.courseEndDate}
                        onChange={(e) => handleChange('educationDetails', 'courseEndDate', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Tuition Fees (Annual)
                      </label>
                      <input
                        type="text"
                        value={formData.educationDetails.tuitionFees}
                        onChange={(e) => handleChange('educationDetails', 'tuitionFees', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., £20,000"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 5: Financial Info */}
              {currentStep === 5 && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">
                      Financial Information
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Provide details about your funding and sponsor
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Total Funds Available *
                      </label>
                      <input
                        type="text"
                        value={formData.financialInfo.totalFundsAvailable}
                        onChange={(e) => handleChange('financialInfo', 'totalFundsAvailable', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., £30,000"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Sponsor Type
                      </label>
                      <select
                        value={formData.financialInfo.sponsorType}
                        onChange={(e) => handleChange('financialInfo', 'sponsorType', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select Type</option>
                        <option value="Self">Self Funded</option>
                        <option value="Parent">Parent</option>
                        <option value="Guardian">Guardian</option>
                        <option value="Scholarship">Scholarship</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Sponsor Name *
                      </label>
                      <input
                        type="text"
                        value={formData.financialInfo.sponsorName}
                        onChange={(e) => handleChange('financialInfo', 'sponsorName', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Relationship to Sponsor
                      </label>
                      <input
                        type="text"
                        value={formData.financialInfo.sponsorRelationship}
                        onChange={(e) => handleChange('financialInfo', 'sponsorRelationship', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Sponsor Occupation
                      </label>
                      <input
                        type="text"
                        value={formData.financialInfo.sponsorOccupation}
                        onChange={(e) => handleChange('financialInfo', 'sponsorOccupation', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Bank Name
                      </label>
                      <input
                        type="text"
                        value={formData.financialInfo.bankName}
                        onChange={(e) => handleChange('financialInfo', 'bankName', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 6: Employment */}
              {currentStep === 6 && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">
                      Employment History
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Optional - provide if you are currently employed
                    </p>
                  </div>

                  <div>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.employmentHistory.currentlyEmployed}
                        onChange={(e) => handleChange('employmentHistory', 'currentlyEmployed', e.target.checked)}
                        className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-3 text-sm font-semibold text-gray-700">
                        I am currently employed
                      </span>
                    </label>
                  </div>

                  {formData.employmentHistory.currentlyEmployed && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Employer Name
                        </label>
                        <input
                          type="text"
                          value={formData.employmentHistory.employerName}
                          onChange={(e) => handleChange('employmentHistory', 'employerName', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Position
                        </label>
                        <input
                          type="text"
                          value={formData.employmentHistory.position}
                          onChange={(e) => handleChange('employmentHistory', 'position', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Start Date
                        </label>
                        <input
                          type="date"
                          value={formData.employmentHistory.startDate}
                          onChange={(e) => handleChange('employmentHistory', 'startDate', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Salary
                        </label>
                        <input
                          type="text"
                          value={formData.employmentHistory.salary}
                          onChange={(e) => handleChange('employmentHistory', 'salary', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Annual salary"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 7: Review & Submit */}
              {currentStep === 7 && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">
                      Review & Submit
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Please review your application before submitting
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Application Summary</h4>
                      <dl className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <dt className="text-gray-600">Country:</dt>
                          <dd className="font-semibold">{formData.country}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-gray-600">Visa Type:</dt>
                          <dd className="font-semibold">{formData.visaType}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-gray-600">Name:</dt>
                          <dd className="font-semibold">{formData.personalInfo.fullName}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-gray-600">University:</dt>
                          <dd className="font-semibold">{formData.educationDetails.universityName}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-gray-600">Course:</dt>
                          <dd className="font-semibold">{formData.educationDetails.courseName}</dd>
                        </div>
                      </dl>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-900">
                        <strong>Important:</strong> After submitting, you will need to upload required documents
                        including passport copies, financial documents, and education certificates.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
                <div className="flex space-x-3">
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={handlePrev}
                      className="px-6 py-3 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-lg font-semibold transition-colors flex items-center"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Previous
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleSaveDraft}
                    disabled={submitting}
                    className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-semibold transition-colors disabled:opacity-50"
                  >
                    Save Draft
                  </button>
                </div>

                {currentStep < 7 ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors flex items-center"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg font-semibold transition-colors flex items-center"
                  >
                    {submitting ? (
                      <>
                        <Loader className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Application
                        <CheckCircle2 className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisaApplicationForm;
